import { getEventTime } from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-types'
import {
  getIncidentsGroupRepresentativeIncident,
  IncidentsGroup,
  isIncidentsGroup,
} from '@desktop/views/Relation/Sports/configuration'

import { IncidentTimeToIncidentsIdMap } from './types'

type IncidentTimeMap = {
  hasSameTimeIncidents: (incident: Incident) => boolean
  updateIncidentTimeToIncidentsIdMap: (incident: Incident) => boolean
  deleteIncidentTimeToIncidentsIdMap: (incident: Incident) => boolean
  addIncidentTimeToIncidentsIdMap: (
    incidents: Incident | IncidentsGroup,
  ) => boolean
}

export function createIncidentTimeMap(): IncidentTimeMap {
  const incidentTimeToIncidentsIdMap: IncidentTimeToIncidentsIdMap = {}

  function getIncidentCompositeId(incidents: Incident | Incident[]): string {
    const values = Array.isArray(incidents) ? incidents : [incidents]

    return values.map((incident) => incident.id).join(',')
  }

  function addIncidentEntry(
    incident: Incident,
    incidentValue = getIncidentCompositeId(incident),
  ): boolean {
    const eventTime = getEventTime(incident)

    if (!incidentTimeToIncidentsIdMap[eventTime]) {
      incidentTimeToIncidentsIdMap[eventTime] = new Set<string>()
    }

    incidentTimeToIncidentsIdMap[eventTime].add(incidentValue)

    return incidentTimeToIncidentsIdMap[eventTime].size > 1
  }

  function hasSameTimeIncidents(incident: Incident): boolean {
    const key = getEventTime(incident)
    const value = incidentTimeToIncidentsIdMap[key]
    const incidentCompositeId = getIncidentCompositeId(incident)

    return value && value.size > 1 && value.has(incidentCompositeId)
  }

  /**
   * Add multiple incidents to the map
   *
   * @returns true if at least one time (map key) has more than 1 incident
   */
  function addIncidentTimeToIncidentsIdMap(
    incident: Incident | IncidentsGroup,
  ): boolean {
    if (isIncidentsGroup(incident)) {
      const representativeIncident =
        getIncidentsGroupRepresentativeIncident(incident)
      const eventTime = getEventTime(representativeIncident)
      const representativeCompositeId = getIncidentCompositeId(
        representativeIncident,
      )

      if (
        incidentTimeToIncidentsIdMap[eventTime] &&
        incidentTimeToIncidentsIdMap[eventTime].has(representativeCompositeId)
      ) {
        incidentTimeToIncidentsIdMap[eventTime].delete(
          representativeCompositeId,
        )
      }

      return addIncidentEntry(
        representativeIncident,
        getIncidentCompositeId(incident.incidents),
      )
    }

    return addIncidentEntry(incident)
  }

  function updateIncidentTimeToIncidentsIdMap(incident: Incident): boolean {
    const eventTime = getEventTime(incident)
    const incidentCompositeId = getIncidentCompositeId(incident)

    if (
      incidentTimeToIncidentsIdMap[eventTime] &&
      incidentTimeToIncidentsIdMap[eventTime].has(incidentCompositeId)
    ) {
      // time didn't change
      return false
    }

    let hasPairChanged = false

    for (const [key, value] of Object.entries(incidentTimeToIncidentsIdMap)) {
      // find previous time of incident
      if (value.has(incidentCompositeId)) {
        value.delete(incidentCompositeId)

        if (value.size) {
          hasPairChanged = true // after delete, entry with time has at least 1 incident
        } else {
          delete incidentTimeToIncidentsIdMap[key]
        }

        break
      }
    }

    return addIncidentEntry(incident) || hasPairChanged
  }

  function deleteIncidentTimeToIncidentsIdMap(incident: Incident): boolean {
    const eventTime = getEventTime(incident)
    const incidentCompositeId = getIncidentCompositeId(incident)

    const value = incidentTimeToIncidentsIdMap[eventTime]

    if (value && value.has(incidentCompositeId)) {
      value.delete(incidentCompositeId)

      return true
    }

    return false
  }

  return {
    hasSameTimeIncidents,
    updateIncidentTimeToIncidentsIdMap,
    deleteIncidentTimeToIncidentsIdMap,
    addIncidentTimeToIncidentsIdMap,
  }
}
